import React from "react"
import { useSnackbar } from "notistack"
import { RoboConfig } from "../../../services/apiRobo"
import { erroMsg } from "../components/comum"

/**
 * Configuração (porte fiel da options.html da extensão). Os campos estão ligados EXATAMENTE às
 * chaves que o backend lê (configService.getX por caminho pontilhado). Sub-abas: Geral (grupos
 * colapsáveis), Prompts, Equipe, Mensagens rápidas e Avançado (JSON cru + testes + backup).
 */

type Tipo = "bool" | "int" | "float" | "long" | "text" | "textarea" | "secret"
interface Campo {
    path: string
    label: string
    tipo: Tipo
    dica?: string
}
interface Grupo {
    titulo: string
    campos: Campo[]
}

const GRUPOS: Grupo[] = [
    {
        titulo: "🧠 Inteligência Artificial e API",
        campos: [
            { path: "ia.model", label: "Modelo principal (Sonnet)", tipo: "text", dica: "vazio = padrão do servidor" },
            { path: "ia.haikuModel", label: "Modelo de triagem (Haiku)", tipo: "text" },
            { path: "ia.aiNoEmojis", label: "IA sem emojis", tipo: "bool" },
            { path: "ia.dailyBudgetUsd", label: "Orçamento diário (US$)", tipo: "float" },
            { path: "ia.monthlyBudgetUsd", label: "Orçamento mensal (US$)", tipo: "float" },
        ],
    },
    {
        titulo: "🤝 Vendedor IA, humanização",
        campos: [
            { path: "robos.salesEnabled", label: "Vendedor IA ligado", tipo: "bool" },
            { path: "comercial.salesResponseDelaySeconds", label: "Atraso da resposta (s)", tipo: "int" },
            { path: "comercial.salesMaxAutoMessagesPerDay", label: "Máx. mensagens automáticas/dia", tipo: "int" },
            { path: "comercial.salesFollowupAfterMinutes", label: "Follow-up após (min, 0=off)", tipo: "int" },
            { path: "comercial.salesMaxFollowups", label: "Máx. follow-ups", tipo: "int" },
            { path: "comercial.salesHotLeadChatId", label: "Chat de aviso de lead quente (id)", tipo: "long" },
            { path: "comercial.salesHotLeadOrdersThreshold", label: "Limiar de pedidos/dia p/ lead quente", tipo: "int" },
            { path: "comercial.salesVoiceReplyEnabled", label: "Responder em áudio", tipo: "bool" },
            { path: "comercial.dmAutoClassifyEnabled", label: "Classificar DM nova automaticamente", tipo: "bool" },
            { path: "comercial.dmAutoLeadConfidenceThreshold", label: "Confiança mín. p/ virar lead (0-1)", tipo: "float" },
            { path: "comercial.humanizeTypingEnabled", label: "Simular digitação", tipo: "bool" },
            { path: "comercial.humanizeTypingMsPerChar", label: "ms por caractere", tipo: "int" },
            { path: "comercial.humanizeTypingMinMs", label: "Digitação mín. (ms)", tipo: "int" },
            { path: "comercial.humanizeTypingMaxMs", label: "Digitação máx. (ms)", tipo: "int" },
        ],
    },
    {
        titulo: "🎧 Sistema de Suporte (Tickets)",
        campos: [
            { path: "suporte.supportEnabled", label: "Suporte por IA ligado", tipo: "bool" },
            { path: "suporte.supportSyncEnabled", label: "Responder suporte na hora (síncrono, sem fila/Quartz)", tipo: "bool", dica: "Classifica/abre ticket/responde dentro do webhook e mostra na tela Conversas WhatsApp" },
            { path: "suporte.supportDmConfidenceThreshold", label: "Confiança mín. p/ abrir suporte em DM (0-1)", tipo: "float" },
            { path: "suporte.supportCategorizationEnabled", label: "Categorizar tickets por IA", tipo: "bool" },
            { path: "suporte.supportPreAttendMaxRepliesPerTicket", label: "Máx. respostas de pré-atendimento/ticket", tipo: "int" },
            { path: "suporte.supportSlaEnabled", label: "SLA ligado", tipo: "bool" },
            { path: "suporte.supportSlaPickupMinutes", label: "SLA de pickup (min)", tipo: "int" },
            { path: "suporte.supportSlaResolveMinutes", label: "SLA de resolução (min)", tipo: "int" },
            { path: "suporte.supportSlaReAlertMinutes", label: "Re-alerta de SLA (min)", tipo: "int" },
        ],
    },
    {
        titulo: "📨 Convites",
        campos: [
            { path: "convites.inviteAgentEnabled", label: "Agente de convite ligado", tipo: "bool" },
            { path: "convites.inviteLink", label: "Link do grupo", tipo: "text" },
            { path: "convites.inviteOriginGroupName", label: "Nome do grupo", tipo: "text" },
            { path: "convites.inviteFirstMessageTemplate", label: "Template da 1ª mensagem (opcional)", tipo: "text" },
            { path: "convites.inviteTemplateIdioma", label: "Idioma do template", tipo: "text" },
            { path: "convites.inviteFirstMessageText", label: "1ª mensagem (texto, se sem template)", tipo: "textarea" },
            { path: "convites.inviteLinkMessageText", label: "Mensagem ao mandar o link", tipo: "textarea" },
            { path: "convites.inviteMaxAiMessages", label: "Máx. mensagens do agente", tipo: "int" },
            { path: "convites.inviteSendIntervalSec", label: "Debounce da resposta (s)", tipo: "int" },
            { path: "convites.inviteAutoMigrateEnabled", label: "Auto-migrar para comercial", tipo: "bool" },
            { path: "convites.inviteAutoMigrateMinScore", label: "Score mín. para migrar (0-10)", tipo: "int" },
            { path: "convites.inviteAutoMigrateMinTimeAfterLinkMin", label: "Tempo mín. após o link (min)", tipo: "int" },
            { path: "convites.inviteToCommercialAfterLinkReply", label: "Migrar ao responder após o link", tipo: "bool" },
            { path: "convites.inviteWelcomeMessageEnabled", label: "Boas-vindas ao entrar", tipo: "bool" },
            { path: "convites.inviteWelcomeMessageText", label: "Texto de boas-vindas", tipo: "textarea" },
        ],
    },
    {
        titulo: "🛡 Comunidade e moderação",
        campos: [
            { path: "comunidade.communityAnalysisEnabled", label: "Análise de comunidade ligada", tipo: "bool" },
            { path: "comunidade.communityPrefilterEnabled", label: "Pré-filtro por palavra-chave", tipo: "bool" },
            { path: "comunidade.communityPrefilterMinChars", label: "Mín. de caracteres p/ analisar", tipo: "int" },
            { path: "comunidade.communityMinBatch", label: "Lote mínimo p/ chamar a IA", tipo: "int" },
            { path: "comunidade.communityMaxWaitMin", label: "Espera máx. antes de analisar (min)", tipo: "int" },
            { path: "comunidade.autoModerationEnabled", label: "Auto-moderação (remover)", tipo: "bool" },
            { path: "comunidade.autoModerationSpamScore", label: "Score de spam p/ remover", tipo: "int" },
            { path: "comunidade.autoModerationHateScore", label: "Score de ódio p/ remover", tipo: "int" },
        ],
    },
    {
        titulo: "🔊 Áudio (transcrição e voz)",
        campos: [
            { path: "audio.audioTranscriptionEnabled", label: "Transcrever áudios recebidos (Whisper)", tipo: "bool" },
            { path: "voz.elevenVoiceId", label: "ElevenLabs — voice id", tipo: "text" },
            { path: "voz.elevenModel", label: "ElevenLabs — modelo", tipo: "text" },
            { path: "voz.elevenLanguage", label: "ElevenLabs — idioma", tipo: "text" },
        ],
    },
    {
        titulo: "📲 Meta & Google",
        campos: [
            { path: "meta.metaSessionWindowHours", label: "Janela de sessão (h)", tipo: "int" },
            { path: "segredos.googlePlacesApiKey", label: "Google Places API key", tipo: "secret" },
            { path: "segredos.elevenLabsApiKey", label: "ElevenLabs API key", tipo: "secret" },
        ],
    },
    {
        titulo: "🪪 Identidade e empresa",
        campos: [
            { path: "ia.agentIdentity", label: "Como o robô se identifica", tipo: "text" },
            { path: "ia.companyContext", label: "Contexto da empresa", tipo: "textarea" },
            { path: "ia.companyWebsite", label: "Site oficial", tipo: "text" },
            { path: "ia.companyInstagram", label: "Instagram oficial", tipo: "text" },
        ],
    },
]

const SUBS = ["Geral", "Prompts", "Equipe", "Mensagens rápidas", "Avançado"]

export function Config() {
    const [sub, setSub] = React.useState(0)
    return (
        <section className="panel active" style={{ display: "block" }}>
            <div className="panel-actions">
                <h3 style={{ margin: 0 }}>⚙ Configuração</h3>
            </div>
            <div className="sub-tabs">
                {SUBS.map((s, i) => (
                    <button key={s} className={"sub-tab" + (sub === i ? " active" : "")} onClick={() => setSub(i)}>
                        {s}
                    </button>
                ))}
            </div>
            <div style={{ padding: 12 }}>
                {sub === 0 && <Geral />}
                {sub === 1 && <Prompts />}
                {sub === 2 && <Equipe />}
                {sub === 3 && <MensagensRapidas />}
                {sub === 4 && <Avancado />}
            </div>
        </section>
    )
}

// ─── Geral (grupos colapsáveis ligados ao config JSON) ───────────────────────
function getPath(o: any, p: string): any {
    return p.split(".").reduce((x, k) => (x == null ? undefined : x[k]), o)
}
function setPath(o: any, p: string, v: any): any {
    const parts = p.split(".")
    const copia = { ...(o || {}) }
    let cur = copia
    for (let i = 0; i < parts.length - 1; i++) {
        cur[parts[i]] = { ...(cur[parts[i]] || {}) }
        cur = cur[parts[i]]
    }
    cur[parts[parts.length - 1]] = v
    return copia
}

function Geral() {
    const { enqueueSnackbar } = useSnackbar()
    const [cfg, setCfg] = React.useState<any>({})
    const [status, setStatus] = React.useState<any>(null)
    const [carregando, setCarregando] = React.useState(true)
    const [salvando, setSalvando] = React.useState(false)
    const [togglando, setTogglando] = React.useState(false)

    async function alternarRobo() {
        if (!status) return
        const ligar = !status.roboAtivo
        if (ligar) {
            const num = status.numeroOficial || status.conta || "(sem número)"
            const ok = window.confirm(
                "Ligar o robô neste estabelecimento?\n\n" +
                    "O robô passará a atender TODAS as mensagens do número oficial " +
                    num +
                    ". Se esse número for o da Izza, ela deixa de responder nele (é um por vez).\n\n" +
                    "Confirme só se este for o número dedicado do robô.",
            )
            if (!ok) return
        }
        setTogglando(true)
        try {
            const r = await RoboConfig.ligarRobo(ligar)
            setStatus((prev: any) => ({ ...(prev || {}), roboAtivo: !!r?.roboAtivo }))
            enqueueSnackbar(r?.roboAtivo ? "Robô ligado." : "Robô desligado.", { variant: "success" })
        } catch (e) {
            enqueueSnackbar(erroMsg(e), { variant: "error" })
        } finally {
            setTogglando(false)
        }
    }

    const carregar = React.useCallback(async () => {
        setCarregando(true)
        try {
            const [c, st] = await Promise.all([RoboConfig.obter(), RoboConfig.status().catch(() => null)])
            setCfg(c || {})
            setStatus(st)
        } catch (e) {
            enqueueSnackbar(erroMsg(e), { variant: "error" })
        } finally {
            setCarregando(false)
        }
    }, [enqueueSnackbar])

    React.useEffect(() => {
        carregar()
    }, [carregar])

    function setCampo(c: Campo, raw: any) {
        let v: any = raw
        if (c.tipo === "int" || c.tipo === "long") v = raw === "" ? null : parseInt(raw, 10)
        else if (c.tipo === "float") v = raw === "" ? null : parseFloat(raw)
        else if (c.tipo === "bool") v = !!raw
        setCfg((prev: any) => setPath(prev, c.path, v))
    }

    async function salvar() {
        setSalvando(true)
        try {
            await RoboConfig.salvar(cfg)
            enqueueSnackbar("Configuração salva.", { variant: "success" })
        } catch (e) {
            enqueueSnackbar(erroMsg(e), { variant: "error" })
        } finally {
            setSalvando(false)
        }
    }

    if (carregando) return <div className="muted small">Carregando…</div>

    return (
        <>
            {status && (
                <div
                    style={{
                        display: "flex",
                        gap: 8,
                        flexWrap: "wrap",
                        alignItems: "center",
                        padding: "8px 10px",
                        marginBottom: 10,
                        background: "var(--panel-2)",
                        border: "1px solid var(--border)",
                        borderRadius: 6,
                    }}
                >
                    <span className="chip" style={status.roboAtivo ? { background: "var(--op-bg)", color: "var(--op)" } : {}}>
                        {status.roboAtivo ? "Robô ATIVO" : "Robô inativo"}
                    </span>
                    <button
                        className={"small" + (status.roboAtivo ? " ghost" : "")}
                        onClick={alternarRobo}
                        disabled={togglando}
                        title={status.roboAtivo ? "Desligar o robô neste estabelecimento" : "Ligar o robô neste estabelecimento"}
                    >
                        {togglando ? "…" : status.roboAtivo ? "Desligar robô" : "Ligar robô"}
                    </button>
                    {status.temNumeroOficial ? (
                        <span className="small">
                            Número oficial: <strong>{status.numeroOficial || status.conta}</strong>
                            {status.conta ? <span className="muted"> (id {status.conta})</span> : null}
                        </span>
                    ) : (
                        <span className="small" style={{ color: "var(--sp)" }}>
                            Sem número oficial neste estabelecimento — configure o número do robô (separado do da Izza).
                        </span>
                    )}
                </div>
            )}
            {GRUPOS.map(g => (
                <details key={g.titulo} open style={{ marginBottom: 10, border: "1px solid var(--border)", borderRadius: 6 }}>
                    <summary style={{ padding: "8px 10px", cursor: "pointer", fontWeight: 600, background: "var(--panel-2)" }}>
                        {g.titulo}
                    </summary>
                    <div style={{ padding: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                        {g.campos.map(c => (
                            <CampoInput key={c.path} campo={c} valor={getPath(cfg, c.path)} aoMudar={v => setCampo(c, v)} />
                        ))}
                    </div>
                </details>
            ))}
            <div style={{ position: "sticky", bottom: 0, padding: "10px 0", background: "var(--panel)" }}>
                <button disabled={salvando} onClick={salvar}>
                    {salvando ? "Salvando…" : "💾 Salvar configuração"}
                </button>
                <button className="ghost" style={{ marginLeft: 8 }} onClick={carregar}>
                    Recarregar
                </button>
            </div>
        </>
    )
}

function CampoInput({ campo, valor, aoMudar }: { campo: Campo; valor: any; aoMudar: (v: any) => void }) {
    const estiloFull: React.CSSProperties = campo.tipo === "textarea" ? { gridColumn: "1 / -1" } : {}
    if (campo.tipo === "bool") {
        return (
            <label className="small" style={{ display: "flex", alignItems: "center", gap: 8, ...estiloFull }}>
                <input type="checkbox" checked={!!valor} onChange={e => aoMudar(e.target.checked)} />
                <span>{campo.label}</span>
            </label>
        )
    }
    return (
        <label className="small muted" style={{ display: "flex", flexDirection: "column", gap: 2, ...estiloFull }}>
            <span>{campo.label}</span>
            {campo.tipo === "textarea" ? (
                <textarea rows={3} value={valor ?? ""} onChange={e => aoMudar(e.target.value)} />
            ) : (
                <input
                    type={campo.tipo === "secret" ? "password" : campo.tipo === "text" ? "text" : "number"}
                    value={valor ?? ""}
                    onChange={e => aoMudar(e.target.value)}
                    placeholder={campo.dica || ""}
                    autoComplete="off"
                />
            )}
        </label>
    )
}

// ─── Prompts ─────────────────────────────────────────────────────────────────
function Prompts() {
    const { enqueueSnackbar } = useSnackbar()
    const [itens, setItens] = React.useState<any[]>([])
    const [edit, setEdit] = React.useState<any>(null)
    const [carregando, setCarregando] = React.useState(true)

    const carregar = React.useCallback(async () => {
        setCarregando(true)
        try {
            setItens(await RoboConfig.listarPrompts())
        } catch (e) {
            enqueueSnackbar(erroMsg(e), { variant: "error" })
        } finally {
            setCarregando(false)
        }
    }, [enqueueSnackbar])

    React.useEffect(() => {
        carregar()
    }, [carregar])

    if (carregando) return <div className="muted small">Carregando…</div>
    return (
        <>
            {(itens || []).map(p => (
                <div
                    key={p.id}
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "8px 0",
                        borderBottom: "1px solid var(--border)",
                    }}
                >
                    <div>
                        <strong className="small">{p.rotulo || p.id}</strong>{" "}
                        {p.override ? <span className="chip op">customizado</span> : null}
                        <div className="muted small">{p.descricao}</div>
                    </div>
                    <button className="ghost" onClick={() => setEdit(p)}>
                        Editar
                    </button>
                </div>
            ))}
            {edit && (
                <EditarPrompt
                    prompt={edit}
                    aoFechar={() => setEdit(null)}
                    aoSalvar={() => {
                        setEdit(null)
                        carregar()
                    }}
                />
            )}
        </>
    )
}

function EditarPrompt({ prompt, aoFechar, aoSalvar }: { prompt: any; aoFechar: () => void; aoSalvar: () => void }) {
    const { enqueueSnackbar } = useSnackbar()
    const [texto, setTexto] = React.useState<string>(prompt.override || prompt.padrao || "")
    return (
        <div className="modal" style={{ display: "flex" }}>
            <div className="modal-content" style={{ maxWidth: 700 }}>
                <div className="modal-header">
                    <strong>{prompt.rotulo || prompt.id}</strong>
                    <button className="ghost-icon-btn" onClick={aoFechar}>
                        ✕
                    </button>
                </div>
                <div className="modal-body">
                    <textarea
                        rows={16}
                        value={texto}
                        onChange={e => setTexto(e.target.value)}
                        style={{ width: "100%", fontFamily: "monospace", fontSize: 12 }}
                    />
                </div>
                <div className="modal-footer">
                    {prompt.override && (
                        <button
                            className="ghost warning"
                            onClick={async () => {
                                try {
                                    await RoboConfig.restaurarPrompt(prompt.id)
                                    enqueueSnackbar("Restaurado ao padrão.", { variant: "success" })
                                    aoSalvar()
                                } catch (e) {
                                    enqueueSnackbar(erroMsg(e), { variant: "error" })
                                }
                            }}
                        >
                            Restaurar padrão
                        </button>
                    )}
                    <span style={{ flex: 1 }} />
                    <button className="ghost" onClick={aoFechar}>
                        Cancelar
                    </button>
                    <button
                        onClick={async () => {
                            try {
                                await RoboConfig.salvarPrompt(prompt.id, texto)
                                enqueueSnackbar("Prompt salvo.", { variant: "success" })
                                aoSalvar()
                            } catch (e) {
                                enqueueSnackbar(erroMsg(e), { variant: "error" })
                            }
                        }}
                    >
                        Salvar
                    </button>
                </div>
            </div>
        </div>
    )
}

// ─── Equipe (usuários) ───────────────────────────────────────────────────────
function Equipe() {
    const { enqueueSnackbar } = useSnackbar()
    const [itens, setItens] = React.useState<any[]>([])
    const [carregando, setCarregando] = React.useState(true)

    const carregar = React.useCallback(async () => {
        setCarregando(true)
        try {
            setItens(await RoboConfig.listarUsuarios())
        } catch (e) {
            enqueueSnackbar(erroMsg(e), { variant: "error" })
        } finally {
            setCarregando(false)
        }
    }, [enqueueSnackbar])

    React.useEffect(() => {
        carregar()
    }, [carregar])

    async function salvarPapel(u: any, papel: string) {
        try {
            await RoboConfig.salvarUsuario({
                fornecedorId: u.fornecedorId,
                nomeExibicao: u.nomeExibicao || u.nome,
                papel,
                ativo: true,
            })
            enqueueSnackbar("Atualizado.", { variant: "success" })
            carregar()
        } catch (e) {
            enqueueSnackbar(erroMsg(e), { variant: "error" })
        }
    }

    if (carregando) return <div className="muted small">Carregando…</div>
    if (!itens || itens.length === 0)
        return <div className="empty">Sem usuários (provisionados no 1º acesso de cada membro).</div>
    return (
        <>
            {itens.map(u => (
                <div
                    key={u.id}
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "8px 0",
                        borderBottom: "1px solid var(--border)",
                    }}
                >
                    <span className="small">
                        {u.nomeExibicao || u.nome || u.usuario || "#" + u.id} <span className="chip">{u.papel || "ATENDENTE"}</span>
                    </span>
                    <span style={{ display: "flex", gap: 6 }}>
                        <button className="ghost" onClick={() => salvarPapel(u, "ADMIN")}>
                            ADMIN
                        </button>
                        <button className="ghost" onClick={() => salvarPapel(u, "ATENDENTE")}>
                            Atendente
                        </button>
                        <button
                            className="ghost warning"
                            onClick={async () => {
                                try {
                                    await RoboConfig.removerUsuario(u.id)
                                    enqueueSnackbar("Removido.", { variant: "success" })
                                    carregar()
                                } catch (e) {
                                    enqueueSnackbar(erroMsg(e), { variant: "error" })
                                }
                            }}
                        >
                            ✕
                        </button>
                    </span>
                </div>
            ))}
        </>
    )
}

// ─── Mensagens rápidas ───────────────────────────────────────────────────────
function MensagensRapidas() {
    const { enqueueSnackbar } = useSnackbar()
    const [itens, setItens] = React.useState<any[]>([])
    const [rotulo, setRotulo] = React.useState("")
    const [texto, setTexto] = React.useState("")
    const [carregando, setCarregando] = React.useState(true)

    const carregar = React.useCallback(async () => {
        setCarregando(true)
        try {
            setItens(await RoboConfig.listarMensagensRapidas())
        } catch (e) {
            enqueueSnackbar(erroMsg(e), { variant: "error" })
        } finally {
            setCarregando(false)
        }
    }, [enqueueSnackbar])

    React.useEffect(() => {
        carregar()
    }, [carregar])

    async function adicionar() {
        if (!rotulo.trim() || !texto.trim()) return
        try {
            await RoboConfig.salvarMensagemRapida({ rotulo: rotulo.trim(), texto: texto.trim(), ativo: true })
            setRotulo("")
            setTexto("")
            carregar()
        } catch (e) {
            enqueueSnackbar(erroMsg(e), { variant: "error" })
        }
    }

    return (
        <>
            <div className="form-row" style={{ gap: 6, marginBottom: 10 }}>
                <input type="text" placeholder="Rótulo" value={rotulo} onChange={e => setRotulo(e.target.value)} />
                <input type="text" placeholder="Texto" value={texto} onChange={e => setTexto(e.target.value)} style={{ flex: 1 }} />
                <button onClick={adicionar} disabled={!rotulo.trim() || !texto.trim()}>
                    ➕ Adicionar
                </button>
            </div>
            {carregando && <div className="muted small">Carregando…</div>}
            {!carregando && (!itens || itens.length === 0) && <div className="empty">Sem mensagens rápidas.</div>}
            {(itens || []).map(m => (
                <div
                    key={m.id}
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "8px 0",
                        borderBottom: "1px solid var(--border)",
                    }}
                >
                    <span className="small">
                        <strong>{m.rotulo}</strong> — {m.texto}
                    </span>
                    <button
                        className="ghost warning"
                        onClick={async () => {
                            try {
                                await RoboConfig.removerMensagemRapida(m.id)
                                carregar()
                            } catch (e) {
                                enqueueSnackbar(erroMsg(e), { variant: "error" })
                            }
                        }}
                    >
                        ✕
                    </button>
                </div>
            ))}
        </>
    )
}

// ─── Avançado: testes + JSON cru + backup ────────────────────────────────────
const PROVEDORES = ["anthropic", "places", "elevenlabs", "meta"]

function Avancado() {
    const { enqueueSnackbar } = useSnackbar()
    const [texto, setTexto] = React.useState("{}")
    const [testando, setTestando] = React.useState("")

    const carregar = React.useCallback(async () => {
        try {
            setTexto(JSON.stringify((await RoboConfig.obter()) || {}, null, 2))
        } catch (e) {
            enqueueSnackbar(erroMsg(e), { variant: "error" })
        }
    }, [enqueueSnackbar])

    React.useEffect(() => {
        carregar()
    }, [carregar])

    async function salvar() {
        let obj
        try {
            obj = JSON.parse(texto)
        } catch {
            enqueueSnackbar("JSON inválido.", { variant: "error" })
            return
        }
        try {
            await RoboConfig.salvar(obj)
            enqueueSnackbar("Salvo.", { variant: "success" })
        } catch (e) {
            enqueueSnackbar(erroMsg(e), { variant: "error" })
        }
    }

    async function testar(p: string) {
        setTestando(p)
        try {
            const r = await RoboConfig.testar(p)
            const ok = r?.ok ?? r?.sucesso
            enqueueSnackbar(`${p}: ${ok ? "OK" : "falhou"}${r?.mensagem ? " — " + r.mensagem : ""}`, {
                variant: ok ? "success" : "warning",
            })
        } catch (e) {
            enqueueSnackbar(`${p}: ${erroMsg(e)}`, { variant: "error" })
        } finally {
            setTestando("")
        }
    }

    function exportar() {
        const blob = new Blob([texto], { type: "application/json" })
        const a = document.createElement("a")
        a.href = URL.createObjectURL(blob)
        a.download = "robo-config.json"
        a.click()
    }

    return (
        <>
            <div className="small muted" style={{ marginBottom: 6 }}>
                Testar integrações
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
                {PROVEDORES.map(p => (
                    <button key={p} className="ghost" disabled={!!testando} onClick={() => testar(p)}>
                        {testando === p ? "testando…" : p}
                    </button>
                ))}
            </div>
            <div className="small muted" style={{ marginBottom: 4 }}>
                Configuração (JSON cru) — segredos vêm mascarados e são preservados ao salvar.
            </div>
            <textarea
                value={texto}
                onChange={e => setTexto(e.target.value)}
                rows={16}
                style={{ width: "100%", fontFamily: "monospace", fontSize: 12 }}
            />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button onClick={salvar}>💾 Salvar</button>
                <button className="ghost" onClick={carregar}>
                    Recarregar
                </button>
                <button className="ghost" onClick={exportar}>
                    ⬇ Exportar JSON
                </button>
            </div>
        </>
    )
}

export default Config
